import { Fragment, lazy, Suspense, type ComponentType } from "react";
import type { SectionType } from "@veo/types";
import type { RenderedSection, SectionProps } from "./SectionRenderer";
import { SectionSkeleton } from "../components/Skeletons";

/**
 * Block type → renderer.
 *
 * The one place a section type is mapped to a component. Adding an eighteenth
 * block means adding a schema in @veo/types and an entry here; no route, no
 * page and no layout needs to know about it.
 *
 * Heavier blocks are lazy-loaded. A legal page pulling in the gallery lightbox
 * code it will never use is exactly the kind of waste that shows up as slow
 * first paint on a 4G connection.
 */

import HeroBlock from "./HeroBlock";
import HeroSliderBlock from "./HeroSliderBlock";
import RichTextBlock from "./RichTextBlock";
import TextImageBlock from "./TextImageBlock";
import ServicesGridBlock from "./ServicesGridBlock";
import FeatureCardsBlock from "./FeatureCardsBlock";
import StatisticsBlock from "./StatisticsBlock";
import ProcessStepsBlock from "./ProcessStepsBlock";
import FaqAccordionBlock from "./FaqAccordionBlock";
import ConsultationCtaBlock from "./ConsultationCtaBlock";
import RiskDisclaimerBlock from "./RiskDisclaimerBlock";
import ArticleListingBlock from "./ArticleListingBlock";
import TestimonialsBlock from "./TestimonialsBlock";
import TeamBlock from "./TeamBlock";
import MottoBlock from "./MottoBlock";

const ImageGalleryBlock = lazy(() => import("./ImageGalleryBlock"));
const ContactFormBlock = lazy(() => import("./ContactFormBlock"));

/**
 * The block types whose renderer is code-split, and which therefore need a
 * Suspense boundary.
 *
 * Every other block is a plain import and renders synchronously. Wrapping
 * those in Suspense as well looks harmless, but it is not: React's streaming
 * SSR treats each boundary as a unit it may defer, so the server flushes a
 * shell full of skeletons and streams the real markup in afterwards to be
 * swapped in. The page still renders, and a crawler that executes JavaScript
 * still sees it — but the initial HTML contains "Loading section" instead of
 * the content, which is a needless weakening of both first paint and what a
 * non-executing crawler reads.
 *
 * Kept as a set here, beside the `lazy()` calls, so adding a code-split block
 * means changing one place rather than two that drift.
 */
const LAZY_SECTION_TYPES = new Set<SectionType>(["imageGallery", "contactForm"]);

type Renderer = ComponentType<SectionProps<never>>;

const REGISTRY: Record<SectionType, Renderer> = {
  hero: HeroBlock as Renderer,
  heroSlider: HeroSliderBlock as Renderer,
  richText: RichTextBlock as Renderer,
  textImage: TextImageBlock as Renderer,
  servicesGrid: ServicesGridBlock as Renderer,
  featureCards: FeatureCardsBlock as Renderer,
  statistics: StatisticsBlock as Renderer,
  processSteps: ProcessStepsBlock as Renderer,
  faqAccordion: FaqAccordionBlock as Renderer,
  testimonials: TestimonialsBlock as Renderer,
  team: TeamBlock as Renderer,
  articleListing: ArticleListingBlock as Renderer,
  consultationCta: ConsultationCtaBlock as Renderer,
  contactForm: ContactFormBlock as Renderer,
  imageGallery: ImageGalleryBlock as Renderer,
  riskDisclaimer: RiskDisclaimerBlock as Renderer,
  motto: MottoBlock as Renderer,
};

export function SectionList({
  sections,
  data = {},
}: {
  sections: RenderedSection[];
  /** Loader-fetched content, keyed by section id. See lib/section-data.ts. */
  data?: Record<string, unknown>;
}) {
  return (
    <>
      {sections.map((section) => {
        const Renderer = REGISTRY[section.sectionType];

        /**
         * An unknown block type renders nothing rather than crashing the page.
         * A CMS can always contain a section this build does not know about —
         * during a deploy, or after a rollback — and a blank gap is a far
         * better outcome than a white screen.
         */
        if (!Renderer) return null;

        const element = (
          <Renderer
            content={section.content as never}
            settings={section.settings}
            data={data[section.id]}
          />
        );

        // Only the code-split blocks get a boundary; the rest render straight
        // into the server's initial HTML.
        if (!LAZY_SECTION_TYPES.has(section.sectionType)) {
          return <Fragment key={section.id}>{element}</Fragment>;
        }

        return (
          <Suspense key={section.id} fallback={<SectionSkeleton />}>
            {element}
          </Suspense>
        );
      })}
    </>
  );
}
